/*
 * This file is part of ng2-json-editor.
 * Copyright (C) 2016 CERN.
 *
 * ng2-json-editor is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License as
 * published by the Free Software Foundation; either version 2 of the
 * License, or (at your option) any later version.
 *
 * ng2-json-editor is distributed in the hope that it will be useful, but
 * WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the GNU
 * General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with ng2-json-editor; if not, write to the Free Software Foundation, Inc.,
 * 59 Temple Place, Suite 330, Boston, MA 02111-1307, USA.
 * In applying this license, CERN does not
 * waive the privileges and immunities granted to it by virtue of its status
 * as an Intergovernmental Organization or submit itself to any jurisdiction.
*/

import {
  Component,
  Input,
  OnChanges,
  OnInit,
  ChangeDetectionStrategy,
  SimpleChanges,
  ChangeDetectorRef,
  TemplateRef
} from '@angular/core';
import { List, Map, Set, Iterable } from 'immutable';

import { AbstractListFieldComponent } from '../abstract-list-field';

import {
  AppGlobalsService,
  JsonStoreService,
  DomUtilService,
  PathUtilService,
  ListPageChangerService,
  ProblemsService,
  EmptyValueService,
} from '../shared/services';
import { LongListNavigatorConfig, JSONSchema, PaginatedItem } from '../shared/interfaces';




export class ComplexListFieldComponent extends AbstractListFieldComponent implements OnChanges, OnInit {

   values: List<Map<string, any>>;
   supportValues: List<Map<string, any>>;
   schema: JSONSchema;
   path: Array<any>;

  paginatedItems: Iterable<number, PaginatedItem>;
  paginatableItems: Iterable<number, PaginatedItem>;

  foundIndices: Array<number>;
  currentFound = 0;
  currentPage = 1;
  findExpression: string;
  navigator: LongListNavigatorConfig;
  shouldDisplayFoundNavigation: boolean;
  private _shouldDisplayOnlyEditFormItems = false;

  constructor(public appGlobalsService: AppGlobalsService,
    public problemsService: ProblemsService,
    public jsonStoreService: JsonStoreService,
    public domUtilService: DomUtilService,
    public pathUtilService: PathUtilService,
    public changeDetectorRef: ChangeDetectorRef,
    public emptyValueService: EmptyValueService,
    public listPageChangerService: ListPageChangerService) {
    super(appGlobalsService, problemsService, jsonStoreService, pathUtilService, changeDetectorRef);
  }

  ngOnInit() {
    super.ngOnInit();
    this.navigator = this.schema.longListNavigatorConfig;
    this.paginatableItems = this.getPaginatableItems();
    this.paginatedItems = this.getPaginatableItemsForPage(this.currentPage);

    if (this.navigator) {
      this.listPageChangerService
        .registerPaginatedList(this.pathString, this.navigator.itemsPerPage)
        .skipWhile(page => page === this.currentPage)
        .takeUntil(this.isDestroyed)
        .subscribe(page => this.onPageChange(page));
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    super.ngOnChanges(changes);

    const valuesChange = changes['values'];
    if (valuesChange && !valuesChange.isFirstChange()) {
      const preSize = valuesChange.previousValue.size;
      const curSize = valuesChange.currentValue.size;

      if (curSize !== preSize) {
        if (this.navigator) {
          // check if element added in the end by comparing the last elements
          const elementAddedToEnd = valuesChange.previousValue.equals(valuesChange.currentValue.pop());
          const lastPage = this.getPageForIndex(curSize - 1);
          // change the page if a new element is added in the end and it's not on the last page
          if (curSize > preSize && this.currentPage !== lastPage && elementAddedToEnd) {
            this.currentPage = lastPage;
          }
        }
      }
      this.paginatableItems = this.getPaginatableItems();
      this.paginatedItems = this.getPaginatableItemsForPage(this.currentPage);
    }
  }

  hasProblemOrPatch(index: number) {
    const itemPath = this.getPathStringForChild(index);
    return this.problemsService.hasProblem(itemPath) || this.jsonStoreService.hasPatchOrChildrenHavePatch(itemPath);
  }

  get headerItemTemplate(): TemplateRef<any> {
    return this.appGlobalsService.templates[this.navigator.headerItemTemplateName];
  }

  onFindClick() {
    // clear for new search
    this.foundIndices = [];
    this.currentFound = 0;
    // search to look for the first match
    if (this.navigator.findSingle) {
      const foundIndex = this.values
        .findIndex(value => this.navigator.findSingle(value, this.findExpression));
      if (foundIndex > -1) {
        this.foundIndices.push(foundIndex);
      }
    }
    // search to look for all matches
    if (this.foundIndices.length === 0 && this.navigator.findMultiple) {
      this.values
        .forEach((value, index) => {
          if (this.navigator.findMultiple(value, this.findExpression)) {
            this.foundIndices.push(index);
          }
        });
    }
    // navigate to first search result if found any
    if (this.foundIndices.length > 0) {
      this.navigateToItem(this.foundIndices[0]);
      this.shouldDisplayFoundNavigation = true;
    } else {
      this.shouldDisplayFoundNavigation = false;
    }
  }

  onAddNewElementAtPosition(index: number) {
    const itemSchema = this.schema.items;
    const emptyValue = this.emptyValueService.generateEmptyValue(itemSchema);
    const values: List<any> = this.jsonStoreService.getIn(this.path);

    let insertIndex;
    if (this.navigator) {
      insertIndex = index + (this.navigator.itemsPerPage * (this.currentPage - 1));
    } else {
      insertIndex = index;
    }
    const insertPath = this.path.concat(insertIndex);
    this.jsonStoreService.addIn(insertPath, emptyValue);
    // focus on the new added element
    const insertPathString = this.pathUtilService.toPathString(insertPath);
    setTimeout(() => {
      this.domUtilService.focusAndSelectFirstEditableChildById(insertPathString);
    });
  }

  onFindInputKeypress(key: string) {
    if (key === 'Enter') {
      this.onFindClick();
    }
  }

  onFoundNavigate(direction: number) {
    // No bound checks, since the buttons are disabled in these cases.
    this.currentFound += direction;
    this.navigateToItem(this.foundIndices[this.currentFound]);
  }

  navigateToItem(index: number) {
    const item = this.paginatableItems.get(index);

    if (!item.editFormDisplayedByUser) {
      item.editFormDisplayedByUser = true;
      this.changeDetectorRef.markForCheck();
    }

    const itemPath = this.path.concat(index);
    const itemId = this.pathUtilService.toPathString(itemPath);

    setTimeout(() => this.domUtilService.focusAndSelectFirstEditableChildById(itemId));
  }

  onPageChange(page: number) {
    this.currentPage = page;
    this.paginatedItems = this.getPaginatableItemsForPage(page);
  }

  getPaginatableItemsForPage(page: number): Iterable<number, PaginatedItem> {
    if (this.navigator) {
      const begin = (page - 1) * this.navigator.itemsPerPage;
      const end = (page * this.navigator.itemsPerPage);
      return this.paginatableItems.slice(begin, end);
    } else {
      return this.paginatableItems;
    }
  }

  getPaginatableItems(): Iterable<number, PaginatedItem> {
    const { viewTemplateConfig } = this.schema;
    return this.values
      .map((value, index) => {
        const paginatableItem = this.paginatableItems ? this.paginatableItems.get(index) : null;
        const isNewItem = paginatableItem == null;
        const editFormDisplayedByUser = isNewItem ? null : paginatableItem.editFormDisplayedByUser;
        const shouldDisplayEditForm = viewTemplateConfig ? viewTemplateConfig.showEditForm(value) : true;
        return { index, shouldDisplayEditForm, editFormDisplayedByUser };
      }).filter(item => {
        if (this.shouldDisplayOnlyEditFormItems) {
          return item.shouldDisplayEditForm;
        } else {
          return true;
        }
      });
  }

  getPageForIndex(index: number): number {
    return Math.floor((index / this.navigator.itemsPerPage) + 1);
  }

  get customTemplate(): TemplateRef<any> {
    return this.appGlobalsService.templates[this.schema.viewTemplateConfig.itemTemplateName];
  }

  get shouldDisplayViewTemplate(): boolean {
    return this.schema.viewTemplateConfig !== undefined;
  }

  get sortable(): boolean {
    return this.schema.sortable && !this.shouldDisplayOnlyEditFormItems;
  }

  shouldDisplayEditableFieldsForItem(item: PaginatedItem): boolean {
    // override default display state that is based on viewTemplateConfig.showEditForm(item) with user action
    const shouldDisplayEditForm = item.editFormDisplayedByUser != null ? item.editFormDisplayedByUser : item.shouldDisplayEditForm;
    return shouldDisplayEditForm || this.hasProblemOrPatch(item.index);
  }

  set shouldDisplayOnlyEditFormItems(value: boolean) {
    this.currentPage = 1;
    this._shouldDisplayOnlyEditFormItems = value;
    this.paginatableItems = this.getPaginatableItems();
    this.paginatedItems = this.getPaginatableItemsForPage(this.currentPage);
  }


  get shouldDisplayOnlyEditFormItems(): boolean {
    return this._shouldDisplayOnlyEditFormItems;
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  selector: 'complex-list-field',
  styles: [".complex-list-field-wrapper { margin: 10px 15px 15px 10px; padding: 5px; box-shadow: 0 0 3px 1px rgba(0, 0, 0, 0.25); } .top-bar-container { width: 100%; position: sticky; top: 46px; z-index: 1; display: flex; align-items: center; justify-content: space-between; padding: 0 15px; background-color: white; box-shadow: 0 0.5px 0 0 #ffffff inset, 0 1px 2px 0 #B3B3B3; } .item-count-label { position: relative; top: -19px; } .transparent { background: transparent; } .borderless { border: none; } .find-button { color: darkslategray; } .find-button .fa-search { opacity: 0.83; } .element-button-container { padding-top: 8px; } .element-button-container .text-right { padding: 0px; } label.btn { color: white !important; } .add-new-element { text-align: right; } "],
  template: "<div [id]=\"pathString\"> <!-- Top Bar: Navigator, ToEdit/All switch, custom header item etc. --> <div *ngIf=\"navigator || shouldDisplayViewTemplate\" class=\"top-bar-container\"> <div *ngIf=\"navigator\"> <div class=\"input-group input-group-sm\"> <span class=\"input-group-btn\"> <button type=\"button\" class=\"btn btn-default find-button\" (click)=\"onFindClick()\"> <i class=\"fa fa-search\" aria-hidden=\"true\"></i> </button> </span> <input type=\"search\" class=\"form-control\" [(ngModel)]=\"findExpression\" (keypress)=\"onFindInputKeypress($event.key)\" placeholder=\"Find\" /> <span class=\"input-group-btn\" *ngIf=\"shouldDisplayFoundNavigation\"> <button type=\"button\" class=\"btn btn-default\" [disabled]=\"currentFound <= 0\" (click)=\"onFoundNavigate(-1)\">❮</button> </span> <span class=\"input-group-btn\" *ngIf=\"shouldDisplayFoundNavigation\"> <button type=\"button\" class=\"btn btn-default\" [disabled]=\"currentFound >= foundIndices.length - 1\" (click)=\"onFoundNavigate(1)\">❯</button> </span> <span *ngIf=\"foundIndices\" [ngSwitch]=\"foundIndices.length\" class=\"input-group-addon transparent borderless\"> <span *ngSwitchCase=\"0\"> Nothing found </span> <span *ngSwitchDefault> {{currentFound + 1}} of {{foundIndices.length}} </span> </span> </div> </div> <div *ngIf=\"shouldDisplayViewTemplate\" class=\"btn-group\"> <label class=\"btn btn-switch\" [class.active]=\"!shouldDisplayOnlyEditFormItems\" (click)=\"shouldDisplayOnlyEditFormItems = false\">All</label> <label class=\"btn btn-switch\" [class.active]=\"shouldDisplayOnlyEditFormItems\" (click)=\"shouldDisplayOnlyEditFormItems = true\">To Edit</label> </div> <div *ngIf=\"headerItemTemplate\"> <ng-template [ngTemplateOutlet]=\"headerItemTemplate\"></ng-template> </div> <div *ngIf=\"navigator\"> <label class=\"item-count-label\"> {{paginatableItems.size}} {{path[path.length - 1]}} </label> <br> <pagination [totalItems]=\"paginatableItems.size\" [ngModel]=\"currentPage\" [maxSize]=\"navigator.maxVisiblePageCount\" [itemsPerPage]=\"navigator.itemsPerPage\" class=\"pagination-sm pagination-top\" [boundaryLinks]=\"true\" [rotate]=\"false\" [firstText]=\"'❮❮'\" [previousText]=\"'❮'\" [nextText]=\"'❯'\" [lastText]=\"'❯❯'\" (pageChanged)=\"onPageChange($event.page)\"></pagination> </div> </div> <div [ngClass]=\"redLeftBorderClass\"> <!-- Elements --> <div *ngFor=\"let item of paginatedItems; index as i; trackBy:trackByElement\"> <div class=\"complex-list-field-wrapper\"> <div class=\"add-new-element\"> <a href=\"javascript:void(0)\" (click)=\"onAddNewElementAtPosition(i)\"> Add new element here </a> </div> <span *ngIf=\"shouldDisplayViewTemplate && values.get(item.index).keySeq().size != 0\"> <ng-template [ngTemplateOutlet]=\"customTemplate\" [ngTemplateOutletContext]=\"{item: values.get(item.index)}\"></ng-template> <a href=\"javascript:void(0)\" (click)=\"item.editFormDisplayedByUser = !shouldDisplayEditableFieldsForItem(item)\"> {{shouldDisplayEditableFieldsForItem(item) ? 'Hide Fields' : 'Show Fields'}} </a> </span> <span *ngIf=\"shouldDisplayEditableFieldsForItem(item)\"> <object-field [value]=\"values.get(item.index)\" [schema]=\"schema.items\" [path]=\"getPathForChild(item.index)\" [isComplexListFieldItem]=\"true\"> </object-field> <div class=\"row element-button-container\"> <div class=\"col-md-12 text-right\"> <list-action-group (onMove)=\"moveElement(item.index, $event)\" (onDelete)=\"deleteElement(item.index, values.get(item.index))\" [canMove]=\"sortable\" [isDisabled]=\"disabled || hasPatchOrChildrenHavePatch()\"> </list-action-group> </div> </div> </span> </div> </div> <div class=\"text-right\" *ngIf=\"removeJsonPatch\"> <patch-actions [patch]=\"removeJsonPatch\"></patch-actions> </div> </div> <div *ngFor=\"let patch of addJsonPatches\" class=\"complex-list-field-wrapper\"> <add-or-replace-patch [patch]=\"patch\"></add-or-replace-patch> </div> <div *ngIf=\"replaceJsonPatches && replaceJsonPatches[0]\"> <add-or-replace-patch [patch]=\"replaceJsonPatches[0]\"></add-or-replace-patch> </div> </div> ",
  changeDetection: ChangeDetectionStrategy.OnPush
}, ] },
];
/** @nocollapse */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: AppGlobalsService, },
{type: ProblemsService, },
{type: JsonStoreService, },
{type: DomUtilService, },
{type: PathUtilService, },
{type: ChangeDetectorRef, },
{type: EmptyValueService, },
{type: ListPageChangerService, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'values': [{ type: Input },],
'supportValues': [{ type: Input },],
'schema': [{ type: Input },],
'path': [{ type: Input },],
};
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
